import {
  AffiliationItemWithMetaData,
  Item,
  ItemWithMetaData,
} from "../api/Items";
import { ReportSummaryItemApi } from "../api/Reports";

const MAX_DESCRIPTION_LIMIT = 2048;
const SOURCE_OPTIONS = [
  { id: "linkedin", label: "LinkedIn" },
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "x", label: "X (Twitter)" },
  { id: "pinterest", label: "Pinterest" },
  { id: "youtube", label: "YouTube" },
  { id: "bluesky", label: "Bluesky" },
  { id: "reddit", label: "Reddit" },
  { id: "tiktok", label: "TikTok" },
  { id: "other", label: "Other" },
];
const ACTIVITY_OPTIONS = [
  { id: "originalPost", label: "Original Post" },
  { id: "likedPost", label: "Liked" },
  { id: "followed", label: "Followed" },
  { id: "shared", label: "Shared" },
  { id: "pinned", label: "Pinned" },
  { id: "profileBanner", label: "Profile Banner" },
];

export { MAX_DESCRIPTION_LIMIT, SOURCE_OPTIONS, ACTIVITY_OPTIONS };

export const ITEM_SOURCE_AFFILIATION = "Affiliation";

type AddAffiliationProps = {
  report: ReportSummaryItemApi;
  behaviors?: string[];
  onClose: (item?: Item) => void;
  affiliationContent?: AffiliationItemWithMetaData;
  isNew?: boolean;
  showDateError?: boolean;
};

type formErrors = {
  affiliationType: boolean;
  affiliationSource: boolean;
  affiliationActivity: boolean;
  url: boolean;
  imageUrl: boolean;
  text: boolean;
  ratings: boolean;
  date: boolean;
  submissionTried: boolean;
};

export type { AddAffiliationProps, formErrors };

export interface OptimizedImageResult {
  blobUrl: string;
  file: File;
}
const MAX_IMAGE_SIZE = 50 * 1024; // 50KB

const createFileFromBlob = (blob: Blob): File => {
  const blobUrl = URL.createObjectURL(blob);
  return new File([blob], blobUrl.split("/").pop() ?? "", { type: blob.type });
};

export const optimizeImage = (blob: Blob): Promise<OptimizedImageResult> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img;
      const maxDimension = 500;

      if (width > height) {
        if (width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        }
      } else {
        if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
      }

      // Create canvas for resizing
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = width;
      canvas.height = height;

      // Draw resized image
      ctx?.drawImage(img, 0, 0, width, height);

      // Compress to JPEG with quality adjustment to meet 30KB limit
      const compressImage = (quality: number) => {
        canvas.toBlob(
          (compressedBlob) => {
            if (compressedBlob) {
              // Size is under 50KB, create URL and resolve
              if (compressedBlob.size <= MAX_IMAGE_SIZE) {
                const blobUrl = URL.createObjectURL(compressedBlob);
                const file = createFileFromBlob(compressedBlob);
                resolve({ blobUrl, file });
              } else if (quality > 0.1) {
                compressImage(quality - 0.1);
              } else {
                // If we can't get under 50KB even with lowest quality,
                // use the current result and log a warning
                const blobUrl = URL.createObjectURL(compressedBlob);
                console.warn(
                  "Image could not be compressed below 50KB",
                  compressedBlob.size
                );
                const file = createFileFromBlob(compressedBlob);
                resolve({ blobUrl, file: file });
              }
            } else {
              reject(new Error("Failed to compress image"));
            }
          },
          "image/jpeg",
          quality
        );
      };

      compressImage(0.9);
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = URL.createObjectURL(blob);
  });
};

function imageUrlExternalToUrlApi(imageUrl: string): string {
  const parts = imageUrl.split("/");
  const imageKey = parts.pop()?.split("?")[0];
  const reportFolder = parts.pop();
  const bucket = parts.pop()?.split(".")[0];
  if (!bucket || !reportFolder || !imageKey) {
    return "";
  }
  return `https://${bucket}/${reportFolder}/${imageKey}`;
}

export { imageUrlExternalToUrlApi };

function itemToAffiliation(
  item: ItemWithMetaData
): AffiliationItemWithMetaData {
  return {
    item: item.item,
    affiliation_activity: item.item.content.affiliation_activity,
    affiliation_source: item.item.content.affiliation_source,
    affiliation_type: item.item.content.affiliation_type,
    created_at: item.item.content.created_at,
    media_urls: item.item.content.media_urls,
    text: item.item.content.text,
    url: item.item.content.url,
    rating: item.rating,
  };
}

export { itemToAffiliation };
